import React, { useEffect, useState } from 'react';
import {
  View, StyleSheet, Text, Pressable,
  Platform,
  Linking,
} from 'react-native';
import Modal from 'react-native-modal';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Style from '../constants/Style';
import Colors from '../constants/Colors';
import DeleteIcon24 from './24pxIcons/DeleteIcon24';
import Button from './Button';
import { logEvent } from './LogEvent';
import RocketIcon from './RocketIcon';

interface UpdateAppModalProps {
  isVisible: boolean,
  hide: () => void,
}

const UpdateAppModal = ({
  isVisible, hide,
}: UpdateAppModalProps) => {
  const paddingBottom: number = useSafeAreaInsets().bottom;
  const [backdropPressHideEnabled, setBackdropPressHideEnabled] = useState<boolean>(true);

  useEffect(() => {
    if (isVisible) {
      setBackdropPressHideEnabled(false);
      const timeout = setTimeout(() => {
        setBackdropPressHideEnabled(true);
        clearTimeout(timeout);
      }, 375);
    }
  }, [isVisible]);

  const onBackdropPress: () => void = () => (backdropPressHideEnabled ? hide() : null);

  return <Modal
    {...{ isVisible, onBackdropPress }}
    onBackButtonPress={hide}
    animationIn='slideInUp'
    animationOut='slideOutDown'
    useNativeDriver={true}
    statusBarTranslucent
    style={[Style.modal, styles.modal]}
    backdropColor={Colors.light.hereworksPrimaryGrey}
    backdropOpacity={0.40}
  >
    <View style={[
      styles.container, { paddingBottom },
    ]}>
      <Text style={styles.title}>{'Update available'}</Text>
      <Pressable
        style={(state: { hovered: boolean, pressed: boolean }) => [
          styles.closeButton, state.hovered && Style.hovered, state.pressed && Style.pressed,
        ]}
        onPress={hide}
      >
        <DeleteIcon24 color={Colors.light.hereworksBlack600} />
      </Pressable>
      <View style={{ justifyContent: 'center', alignItems: 'center' }}>
        <RocketIcon style={{
          shadowOffset: {
            width: 2,
            height: 10,
          },
          shadowOpacity: 0.15,
          shadowRadius: 12,
          shadowColor: 'black',
          elevation: 5,
        }} />
        <Text style={{
          fontSize: 16,
          lineHeight: 20,
          fontFamily: 'Poppins_500Medium',
          color: Colors.light.hereworksBlack600,
          textAlign: 'center',
          marginTop: 24,
        }}>New update available</Text>
        <Text style={{
          fontSize: 16,
          lineHeight: 24,
          fontFamily: 'Roboto_400Regular',
          color: Colors.light.hereworksBlack400,
          textAlign: 'center',
          marginTop: 4,
        }}>Please update application to the latest version</Text>
      </View>
      <Button
        title='Update'
        affirmative
        onPress={() => {
          logEvent('UpdateAppModal', 'update_button');
          if (Platform.OS === 'ios') Linking.openURL('https://apps.apple.com/ie/app/hereworks-insights/id1634396991');
          else Linking.openURL('https://play.google.com/store/apps/details?id=hereworks.apk');
        }}
        style={styles.applyButton}
      />
      <Button
        title='Not now'
        onPress={() => {
          logEvent('UpdateAppModal', 'not_now');
          hide();
        }}
        style={styles.applyButton}
      />
    </View>
  </Modal>;
};

const styles = StyleSheet.create({
  modal: {
    justifyContent: 'flex-end',
  },
  container: {
    backgroundColor: '#FFF',
    borderTopStartRadius: 12,
    borderTopEndRadius: 12,
    padding: 12,
    paddingTop: 20,
  },
  title: {
    fontSize: 14,
    lineHeight: 17.5,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_600SemiBold',
    textAlign: 'center',
    alignSelf: 'center',
  },
  list: {
    flex: 1,
    marginTop: 12,
  },
  closeButton: {
    position: 'absolute',
    end: 24,
    top: 16,
  },
  applyButton: {
    flex: 0,
    alignSelf: 'stretch',
    margin: 12,
  },
});

export default UpdateAppModal;
